import { Observable, map } from 'rxjs';

import { DatabaseService } from 'src/app/servicios/database/database.service';
import {
  PermissionTypes,
  PermissionsService,
} from 'src/app/servicios/permissions/permissions.service';

/** Submódulo Ayudas en BD Smartcity (`data.permission` en `/admin/ayudas`). */
const HELP_SUBMODULE_ID = 40;

export interface HelpPermissionContext {
  /** Lista de submódulos con acceso de ruta (dato43) */
  routeSubmoduleIds?: string[];
}

function submoduleIdFromApiRow(item: Record<string, unknown>): number | null {
  const raw = item?.['idSubmodulo'] ?? item?.['IdSubmodulo'];
  const id = Number(raw);
  return Number.isFinite(id) ? id : null;
}

function hasGranularHelpPermission(
  permissions: PermissionsService,
  types: PermissionTypes[],
): boolean {
  return types.some((type) => permissions.hasPermissionTo(type, HELP_SUBMODULE_ID));
}

export function canEditHelp(
  permissions: PermissionsService,
  context: HelpPermissionContext = {},
): boolean {
  const editTypes = [
    PermissionTypes.LECTURA,
    PermissionTypes.ESCRITURA,
    PermissionTypes.CREACION,
  ];

  if (hasGranularHelpPermission(permissions, editTypes)) {
    return true;
  }

  const routeIds = context.routeSubmoduleIds ?? [];
  if (routeIds.includes(String(HELP_SUBMODULE_ID))) {
    return true;
  }

  return false;
}

export function canCreateHelp(
  permissions: PermissionsService,
  context: HelpPermissionContext = {},
): boolean {
  const createTypes = [
    PermissionTypes.CREACION,
    PermissionTypes.ESCRITURA,
    PermissionTypes.LECTURA,
  ];

  if (hasGranularHelpPermission(permissions, createTypes)) {
    return true;
  }

  return canEditHelp(permissions, context);
}

/** Aplica en PermissionsService la respuesta de getSubmodulesPermissions. */
export function applySubmodulePermissionsFromApi(
  permissions: PermissionsService,
  response: unknown,
): void {
  if (!Array.isArray(response)) {
    return;
  }

  response.forEach((item: Record<string, unknown>) => {
    const id = submoduleIdFromApiRow(item);
    if (id == null) {
      return;
    }

    const permisos: number[] = item?.['permisos']
      ? String(item['permisos'])
          .split(',')
          .map((p) => Number(p))
          .filter((p) => !Number.isNaN(p))
      : [];

    permissions.setRoutePermissions(permisos, id);

    let especiales: string[] = [];
    const raw = item?.['permisos_especiales'];
    if (Array.isArray(raw)) {
      especiales = raw
        .map((p: { key_identity?: string }) => p?.key_identity)
        .filter((k): k is string => !!k);
    } else if (typeof raw === 'string' && raw.trim()) {
      try {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) {
          especiales = parsed
            .map((p: { key_identity?: string }) => p?.key_identity)
            .filter((k): k is string => !!k);
        }
      } catch {
        especiales = [];
      }
    }

    permissions.setRouteSpecialPermissions(especiales, id);
  });
}

export function preloadHelpPermissions(
  database: DatabaseService,
  permissions: PermissionsService,
): Observable<void> {
  return database.getSubmodulesPermissions([HELP_SUBMODULE_ID]).pipe(
    map((response) => {
      applySubmodulePermissionsFromApi(permissions, response);
    }),
  );
}

export function routeSubmoduleIdsFromSession(dato43: unknown): string[] {
  if (dato43 == null || dato43 === '') {
    return [];
  }
  return String(dato43)
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
}
